import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";
import { bookingQueries } from "../booking/booking.queries";

// Server state of the availabilities of the brands (50-api/booking.md §2.2): when each brand of a campaign receives
// customers, by brand and first day.

const availabilities = api.booking["brand-availabilities"];

type ListPage = InferResponseType<typeof availabilities.$get, 200>;
export type BrandAvailability = ListPage["items"][number];

const PAGE_SIZE = 50;
/** The largest page: the brands of a campaign are read from it. */
const MAX_PAGE_SIZE = 200;

const list = (salesCampaignId: string, page: number, pageSize: number, signal: AbortSignal) =>
  unwrap(
    availabilities.$get(
      { query: { salesCampaignId, page: String(page), pageSize: String(pageSize) } },
      { init: { signal } },
    ),
  );

const keys = { all: [...bookingQueries.all, "availabilities"] as const };

export const availabilityQueries = {
  ...keys,

  list: (salesCampaignId: string) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "list", salesCampaignId],
      queryFn: ({ pageParam, signal }) => list(salesCampaignId, pageParam, PAGE_SIZE, signal),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** The brands that receive customers for a campaign, by name: the ones an appointment can be booked with. */
  brands: (salesCampaignId: string) =>
    queryOptions({
      queryKey: [...keys.all, "brands", salesCampaignId],
      queryFn: async ({ signal }) => {
        const { items } = await list(salesCampaignId, 1, MAX_PAGE_SIZE, signal);
        const names = new Map(items.map((a) => [a.brandOrgId, a.brand]));
        return [...names].map(([orgId, name]) => ({ orgId, name }));
      },
    }),
};
